import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mintDocumentViewUrl } from '../services/documentViewUrl';
import { useToast } from '../components/ToastProvider';
import {
  ACTIVE_FETCH_STATUSES,
  documentFetchService,
  type OriginalFetchJob,
} from '../services/documentFetchService';

const POLL_INTERVAL_MS = 3000;
const COMPLETION_TOAST_TTL_MS = 15000;

/**
 * Tracks the background original-document fetch jobs of one KB: seeds active
 * jobs on mount (reload-resume), polls them, and fires the completion toast
 * (with an "open document" link — no auto-open, browsers would block it).
 *
 * All job state lives here, keyed by document id, behind a narrow interface —
 * a future downloads-center panel becomes a new consumer of the same service,
 * not a rewrite of the KB page.
 */
export function useOriginalFetchJobs(kbId: string | undefined) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [fetchJobs, setFetchJobs] = useState<Record<string, OriginalFetchJob>>({});

  // Opened at click time with a fresh short-lived, single-document token (never the
  // session JWT in a URL; a token minted when the toast appeared could already be expired).
  const openPdf = useCallback(
    async (documentId: string) => {
      if (!kbId) return;
      const win = window.open('', '_blank');
      try {
        const url = await mintDocumentViewUrl(kbId, documentId);
        if (win) win.location.href = url;
        else window.open(url, '_blank');
      } catch {
        win?.close();
      }
    },
    [kbId]
  );

  const notifyTerminal = useCallback(
    (job: OriginalFetchJob) => {
      const title = t('kbDocuments.functions.original');
      if (job.status === 'success') {
        addToast({
          type: 'success',
          title,
          message: t('kbDocuments.fetchComplete'),
          linkAction: () => openPdf(job.document_id),
          linkLabel: t('kbDocuments.fetchOpenDocument'),
          ttlMs: COMPLETION_TOAST_TTL_MS,
        });
      } else if (job.status === 'no_pdf') {
        if (job.result?.source_url) {
          addToast({
            type: 'info',
            title,
            message: t('kbDocuments.scipubNoPdf'),
            linkUrl: job.result.source_url,
            linkLabel: t('kbDocuments.scipubOpenSource'),
            ttlMs: COMPLETION_TOAST_TTL_MS,
          });
        } else {
          addToast({ type: 'info', title, message: t('kbDocuments.originalNotAvailable') });
        }
      } else if (job.status === 'failed') {
        addToast({ type: 'error', title, message: t('kbDocuments.fetchFailed') });
      }
      // 'cancelled': the user asked for it, no toast needed
    },
    [addToast, t, openPdf]
  );

  // Reload-resume: pick up jobs still running from a previous page view.
  // Merge (existing entries win) — a job started while this request was in
  // flight must not be wiped by the stale snapshot.
  useEffect(() => {
    if (!kbId) return;
    documentFetchService
      .listActive(kbId)
      .then(jobs => setFetchJobs(prev => ({ ...Object.fromEntries(jobs.map(j => [j.document_id, j])), ...prev })))
      .catch(err => console.error('Error loading active fetch jobs:', err));
  }, [kbId]);

  /** Returns 'already_stored' when the PDF exists server-side, else 'started'. */
  const startFetch = useCallback(
    async (documentId: string): Promise<'already_stored' | 'started'> => {
      if (!kbId) throw new Error('No KB id');
      const resp = await documentFetchService.start(kbId, documentId);
      if (resp.status === 'already_stored') return 'already_stored';
      setFetchJobs(prev => ({ ...prev, [documentId]: resp.job }));
      return 'started';
    },
    [kbId]
  );

  const cancelFetch = useCallback(
    async (documentId: string) => {
      if (!kbId) return;
      try {
        await documentFetchService.cancel(kbId, documentId);
        setFetchJobs(prev =>
          prev[documentId] ? { ...prev, [documentId]: { ...prev[documentId], status: 'cancelling' } } : prev
        );
      } catch (err) {
        console.error('Error cancelling fetch job:', err);
      }
    },
    [kbId]
  );

  // Polling: one batched listActive call per tick. The map only ever holds
  // active jobs, so the effect keys on whether any exist (a boolean) — the
  // interval is NOT torn down on every progress update. jobsRef gives the tick
  // the current map without being a dependency; busyRef prevents overlapping
  // ticks when the backend is slow.
  const jobsRef = useRef(fetchJobs);
  jobsRef.current = fetchJobs;
  const busyRef = useRef(false);
  const hasActiveJobs = Object.keys(fetchJobs).length > 0;

  useEffect(() => {
    if (!kbId || !hasActiveJobs) return;
    const timer = window.setInterval(async () => {
      if (busyRef.current) return;
      busyRef.current = true;
      try {
        const activeList = await documentFetchService.listActive(kbId);
        const next: Record<string, OriginalFetchJob> = Object.fromEntries(
          activeList.map(j => [j.document_id, j])
        );
        // Jobs we track but the KB listing no longer returns: either finished
        // (fetch the terminal state for the toast) or attached to another KB
        // sharing the document (still active — keep tracking it).
        for (const docId of Object.keys(jobsRef.current)) {
          if (next[docId]) continue;
          try {
            const latest = await documentFetchService.getForDocument(kbId, docId);
            if (ACTIVE_FETCH_STATUSES.includes(latest.status)) {
              next[docId] = latest;
            } else {
              notifyTerminal(latest);
            }
          } catch (err) {
            console.error('Error polling fetch job:', err);
          }
        }
        setFetchJobs(prev => {
          const prevIds = Object.keys(prev);
          const unchanged =
            prevIds.length === Object.keys(next).length &&
            prevIds.every(id => {
              const a = prev[id];
              const b = next[id];
              return (
                b !== undefined &&
                a.status === b.status &&
                a.progress.done === b.progress.done &&
                a.progress.total === b.progress.total
              );
            });
          return unchanged ? prev : next;
        });
      } catch (err) {
        console.error('Error polling fetch jobs:', err);
      } finally {
        busyRef.current = false;
      }
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [kbId, hasActiveJobs, notifyTerminal]);

  return { fetchJobs, startFetch, cancelFetch };
}
